import React from "react";

/**
 * CopyrightPolicy Component
 * Formatted structural layout for the Atomic Media Copyright & Takedown Policy.
 */
const CopyrightPolicy202605 = ({ isDarkMode }) => {
  return (
    <div className="space-y-6 text-left">
      
      {/* HEADER SECTION */}
      <div className="text-center border-b pb-4 border-zinc-200 dark:border-zinc-800">
        <h2 className="text-base font-black uppercase tracking-tight text-[#ff00cc]">
          Copyright &amp; Takedown Policy
        </h2>
        <p className="text-[10px] font-bold uppercase tracking-wider mt-1 opacity-60">
          Last Updated: May 2026
        </p>
        <p className="text-[10px] leading-relaxed mt-3 max-w-xl mx-auto italic">
          Atomic Media (“Atomic”) operates as a Direct Licensing platform dedicated to supporting original indie and regional Australian recording artists. In accordance with the Copyright Act 1968 (Cth), Atomic maintains strict policies to prevent intellectual property infringement on our services.
        </p>
      </div>

      {/* SAFE HARBOUR LEGAL NOTICE */}
      <div className={`p-3 rounded-xl border text-[10px] leading-relaxed ${
        isDarkMode 
          ? "bg-zinc-900/40 border-zinc-800 text-zinc-400" 
          : "bg-amber-50/60 border-amber-200 text-amber-900"
      }`}>
        <strong>Notice:</strong> Atomic acts as an online hosting service provider. Content is uploaded directly at the direction of platform users. Under the Australian Online Service Provider Safe Harbour Scheme (Part V Division 2AA of the Copyright Act 1968), Atomic is immune from financial liability for user-generated copyright infringements provided we adhere to the Notice and Takedown procedures outlined below.
      </div>

      {/* SECTION 1 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">1. Prohibited Content on Atomic</h4>
        <p className="mb-2">
          Atomic strictly prohibits the upload of unauthorized third-party materials. Content creators on our platform contractually warrant that their material is 100% original. Atomic does NOT allow:
        </p>
        <ul className="list-disc ml-4 space-y-1 mb-2 font-bold text-red-500 dark:text-red-400">
          <li>Cover Songs (re-recorded versions of compositions owned/written by third parties).</li>
          <li>Unauthorized Samples, mashups, or third-party audio loops.</li>
          <li>Remixes of other artists' tracks unless you upload a explicit, written, uploaded copyright clearance.</li>
          <li className="text-zinc-600 dark:text-zinc-400 font-normal">
            Material where the public performance or communication rights have been assigned to a Collecting Society (e.g., APRA AMCOS, PPCA, ASCAP, BMI) unless a valid "Licence Back" or "Opt-Out" agreement has been executed.
          </li>
        </ul>
      </div>

      {/* SECTION 2 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">2. Notice of Claimed Infringement (Takedown Notice)</h4>
        <p className="mb-2">
          If you are a copyright owner, an exclusive licensee, or an authorized agent thereof, and you believe that content hosted on the Atomic platform infringes your copyright or breaches our direct licensing protocols, you may submit a formal Takedown Notice to our Designated Copyright Agent.
        </p>
        <p className="mb-1 font-bold">To be processed, your notice must be sent via email and must contain the following information:</p>
        <ul className="list-none pl-2 space-y-2">
          <li>
            <strong>• Identification of the Protected Work:</strong> The title of the song, artist name, and clear description of the musical work or sound recording claimed to have been infringed.
          </li>
          <li>
            <strong>• Identification of the Infringing Material:</strong> The specific URL(s) or link(s) on the Atomic platform where the allegedly infringing material resides.
          </li>
          <li>
            <strong>• Contact Information:</strong> Your full legal name, company/organization name (if applicable), physical address, telephone number, and email address.
          </li>
          <li>
            <strong>• Authority Statement:</strong> A statement confirming that you are the lawful copyright owner, exclusive licensee, or an agent explicitly authorized to act on their behalf.
          </li>
          <li>
            <strong>• Good Faith Belief Statement:</strong> A statement that you believe, in good faith, that the use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law.
          </li>
        </ul>
      </div>

      {/* SECTION 3 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">3. Designated Copyright Agent Contact</h4>
        <p className="mb-2">
          All notices under the Copyright Act 1968 (Cth) must be directed to Atomic's appointed representative:
        </p>
        <div className={`p-4 rounded-xl font-mono text-[10px] space-y-1.5 border transition-all ${
          isDarkMode 
            ? "bg-zinc-950 border-zinc-800 text-zinc-300" 
            : "bg-zinc-50 border-zinc-200 text-slate-700"
        }`}>
          <div><span className="opacity-40 font-bold mr-2">TITLE:</span> Designated Copyright Compliance Officer</div>
          <div><span className="opacity-40 font-bold mr-2">ENTITY:</span> Atomic Media</div>
          <div><span className="opacity-40 font-bold mr-2">EMAIL:</span> info@atomicmusic.space (Subject line: ATTN: COPYRIGHT CLAIM)</div>
          <div><span className="opacity-40 font-bold mr-2">ADDRESS:</span> PO Box 187 Jindera, NSW 2642</div>
        </div>
      </div>

      {/* SECTION 4 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">4. Atomic’s Takedown &amp; Investigation Protocol</h4>
        <p className="mb-2">
          Upon receipt of a valid, verifiable Takedown Notice, Atomic will act expeditiously (typically within 24 to 48 hours) to secure the platform:
        </p>
        <ul className="list-none pl-2 space-y-1">
          <li><strong>(a)</strong> Access to the disputed audio track or media will be immediately suspended/disabled.</li>
          <li><strong>(b)</strong> The uploading Artist will be formally notified of the claim via their registered email address.</li>
          <li><strong>(c)</strong> <strong>Withholding of Funds:</strong> Any accumulated tips or subscription royalties associated with the disputed content will be frozen in the platform ledger pending final resolution.</li>
        </ul>
      </div>

      {/* SECTION 5 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">5. Counter-Notifications</h4>
        <p className="mb-2">
          If an Artist believes their track was suspended due to a mistake, misidentification, or a wrongful claim, they may submit a counter-notice to info@atomicmusic.space containing proof of copyright ownership or valid licensing clearance (e.g., proof of an executed APRA AMCOS Licence-Back).
        </p>
        <p className="mb-2">
          Atomic will review the evidence supplied. However, in all circumstances under our platform terms, Atomic's administrative decision regarding content retention or permanent removal is final.
        </p>
      </div>

      {/* SECTION 6 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">6. Repeat Infringer Policy</h4>
        <p className="mb-2">
          Atomic takes intellectual property theft seriously. In compliance with statutory Safe Harbour obligations, Atomic enforces a strict "Three Strikes" Policy.
        </p>
        <p className="mb-2 font-bold text-red-500 dark:text-red-400">
          Any user or artist account that incurs three (3) separate, verified copyright infringement claims or unauthorized cover song uploads will have their profile permanently terminated, their database catalog deleted, and any accrued financial balances contractually forfeited to the Atomic Community Music Fund.
        </p>
      </div>

      {/* FOOTER SIGN-OFF */}
      <div className="py-6 text-center font-black text-[#ff00cc] tracking-widest text-[10px]">
        --- END OF POLICY ---
      </div>
    </div>
  );
};

export default CopyrightPolicy202605;